import { z } from "zod";
// Cortex console wire contracts (ADR-0010, ADR-0040). Operational counts over
// the decision record and the tick cursor: is the loop running, and what is
// it writing. Nothing here claims an effect; lift has its own contract later.
export const CortexLoopHealthQuery = z.object({
    /** Window to count over, in hours. Default 24, at most a week. */
    hours: z.coerce.number().int().min(1).max(168).optional(),
});
export const CortexPlayHoldShare = z.object({
    rule: z.string(),
    sends: z.number(),
    holds: z.number(),
    /** holds over sends plus holds, or null with no randomised rows. */
    holdShare: z.number().nullable(),
});
export const CortexLoopHealthResponse = z.object({
    generatedAt: z.string(),
    windowHours: z.number(),
    sinceIso: z.string(),
    members: z.object({
        /** Members with a heartbeat state row. */
        total: z.number(),
        /** Members whose last tick fell inside the window. */
        tickedInWindow: z.number(),
        /** Members not ticked for longer than the cadence plus slack. A quiet member still ticks, so this counts scheduler gaps. */
        stale: z.number(),
        staleAfterMinutes: z.number(),
    }),
    decisions: z.object({
        sent: z.number(),
        held: z.number(),
        stopped: z.number(),
        byStopReason: z.record(z.string(), z.number()),
        /** Sends that bypassed the coin: a play scored on the member's tap, or one the ring could not read. */
        bypassed: z.number(),
        /** Held over randomised sends plus holds, or null with none. */
        holdShare: z.number().nullable(),
        holdFloor: z.number(),
    }),
    plays: z.array(CortexPlayHoldShare),
    delivery: z.object({
        delivered: z.number(),
        seen: z.number(),
        /** Every acted row. The two below split it; rows older than the source column are in neither. */
        acted: z.number(),
        /** The member tapped the card. */
        actedByMember: z.number(),
        /** The scorer closed the card because the passive reading met the target. */
        actedByScorer: z.number(),
        dismissed: z.number(),
        expired: z.number(),
    }),
    scorer: z.object({
        openWindows: z.number(),
        deferred: z.number(),
        closedInWindow: z.number(),
        lagSecondsP50: z.number().nullable(),
        lagSecondsP99: z.number().nullable(),
        oldestDeferredSeconds: z.number().nullable(),
    }),
    /** Rows resolved in the window, by resolution reason. */
    resolutions: z.record(z.string(), z.number()),
    /** What this page cannot show and where to find it instead. */
    notes: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// Scorer queue: windows waiting to be scored, and why. Fleet-wide, no member.
// ---------------------------------------------------------------------------
export const CortexQueueState = z.enum(["deferred", "due", "open"]);
export const CortexScorerQueueQuery = z.object({
    state: CortexQueueState.optional(),
    metric: z.string().min(1).max(40).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
export const CortexScorerQueueRow = z.object({
    /** The decision record id. Not a member id: a member needs the trace, and ops:member_data. */
    id: z.string(),
    rule: z.string(),
    metric: z.string().nullable(),
    assignment: z.string(),
    offeredAt: z.string(),
    windowEndsAt: z.string(),
    /** Earliest the scorer looks again; null when the window's end is the only gate. */
    nextAttemptAt: z.string().nullable(),
    state: CortexQueueState,
    /** What the row is waiting for, in words, from its metric. */
    waitingOn: z.string(),
    /** Seconds since the window closed, negative while it is still open. */
    ageSeconds: z.number(),
});
export const CortexScorerQueueResponse = z.object({
    generatedAt: z.string(),
    counts: z.object({ deferred: z.number(), due: z.number(), open: z.number() }),
    rows: z.array(CortexScorerQueueRow),
});
// ---------------------------------------------------------------------------
// Decision trace: one record, end to end. Behind ops:member_data and a tenant
// check, because the reading and the outcome are one member's.
// ---------------------------------------------------------------------------
/**
 * Why a send stayed on the Today card with no notification: the day's push
 * cap, or a cooldown after an earlier push (named in `after`). Null on a push,
 * on a hold, and on rows written before the field.
 */
export const CortexCardOnly = z.object({
    reason: z.enum(["daily_cap", "cooldown", "other"]),
    after: z.string().nullable(),
});
export const CortexDecisionTraceResponse = z.object({
    id: z.string(),
    /** The member, masked, for display. */
    memberRef: z.string(),
    /**
     * The member's id, so the trace can open the member timeline. The caller
     * has already passed ops:member_data and the tenant check to read this
     * row at all, and the timeline sits behind the same two controls.
     */
    memberId: z.string(),
    decided: z.object({
        at: z.string(),
        rule: z.string(),
        behaviour: z.string().nullable(),
        assignment: z.string(),
        stopReason: z.string().nullable(),
        sendProbability: z.number().nullable(),
        available: z.boolean(),
        channel: z.string(),
        variant: z.unknown().nullable(),
        snapshot: z.unknown().nullable(),
        contract: z.unknown().nullable(),
        /** What the row is scored on. Rows older than the frozen contract carry only this. Absent from a server older than the field. */
        outcomeMetric: z.string().nullable().optional(),
        staged: z.boolean(),
    }),
    tick: z
        .object({
        tickedAt: z.string(),
        window: z.string().nullable(),
        cohort: z.string(),
        shortCircuitReason: z.string().nullable(),
        agentAction: z.string(),
        fired: z.boolean(),
        surfaced: z.boolean(),
    })
        .nullable(),
    delivered: z.object({
        at: z.string().nullable(),
        copySource: z.string().nullable(),
        /** Absent from a server older than the field. */
        cardOnly: CortexCardOnly.nullable().optional(),
    }),
    seen: z.object({ at: z.string().nullable() }),
    outcome: z.object({ value: z.string().nullable(), at: z.string().nullable() }),
    resolved: z.object({
        at: z.string().nullable(),
        reason: z.string().nullable(),
        windowEndsAt: z.string().nullable(),
        nextAttemptAt: z.string().nullable(),
        observed: z.number().nullable(),
        baseline: z.number().nullable(),
        score: z.number().nullable(),
        reward: z.number().nullable(),
        /** The outcome in the metric's own unit (steps, active minutes, minutes to sleep, workout minutes). Null where the metric has none. Absent from a server older than the field. */
        magnitude: z.number().nullable().optional(),
        /** What the closing reading counted: steps, active_minutes, minutes_to_sleep, workout_minutes. Null when nothing was read and on rows resolved before the field. */
        readingUnit: z.string().nullable().optional(),
        /** prior_window or hour_of_day, on steps rows. */
        baselineMethod: z.string().nullable().optional(),
    }),
});
// ---------------------------------------------------------------------------
// Playbook performance: lift per variant against its held control, under the
// analysis contract in @healthos/moments/lift. Fleet-wide, no member on the
// wire: the member id is used for clustering on the server and never leaves.
// ---------------------------------------------------------------------------
/** The state snapshot fields a split may be asked for. The values of each are a small closed set. */
export const CortexSnapshotBucket = z.enum([
    "timeOfDay",
    "dayType",
    "wearable",
    "receptivity",
    "budget",
    "tenure",
    "pushAllowed",
    "cohort",
    "pace",
    "week",
]);
export const CortexPerformanceQuery = z.object({
    /** Window to read over, in days. Default 30, at most 90: every row in it is read into memory. */
    days: z.coerce.number().int().min(1).max(90).optional(),
    /** Narrow the bucket split to one variant. The table is always every variant. */
    variantId: z.string().min(1).max(64).optional(),
    /** Ask for the same estimate split by one snapshot bucket. */
    bucket: CortexSnapshotBucket.optional(),
    /**
     * Company-switcher narrowing for platform callers: only that company's
     * members' rows. A plain string, as in `OperationsCompanyScopedQuery`:
     * `effectiveOpsCaller` decides what counts as an id, and anything else
     * leaves the caller unscoped.
     */
    companyId: z.string().optional(),
});
/**
 * How one arm's windows closed, by the scorer's reason, with `open` for rows
 * the scorer has not reached and `no_reason` for rows resolved before the
 * reason column existed. The keys mirror `RESOLUTION_REASONS` in
 * `@healthos/moments`.
 */
export const CortexResolutionMix = z.object({
    scored: z.number(),
    self_reported: z.number(),
    unmeasured_hold: z.number(),
    unseen_send: z.number(),
    no_reading: z.number(),
    no_baseline: z.number(),
    play_retired: z.number(),
    open: z.number(),
    no_reason: z.number(),
});
/** Rows left out of the contrast, by the reason `liftExclusion` gives. */
export const CortexLiftExclusions = z.object({
    staged: z.number(),
    unavailable: z.number(),
    not_randomised: z.number(),
    bypassed_coin: z.number(),
    no_probability: z.number(),
    self_reported: z.number(),
    unscored: z.number(),
});
/** The same contrast on the outcome in the metric's own unit, beside the 0 to 1 score. */
export const CortexMagnitudeLift = z.object({
    unit: z.enum(["steps", "active_minutes", "minutes_to_sleep", "workout_minutes"]),
    /** Which direction is the member doing better. Sleep onset is better sooner, so its lift reads negative. */
    better: z.enum(["higher", "lower"]),
    sends: z.number(),
    holds: z.number(),
    /** Both arms at or above the minimum rows carrying a magnitude. The effect fields are null when false. */
    enough: z.boolean(),
    meanSent: z.number().nullable(),
    meanHeld: z.number().nullable(),
    lift: z.number().nullable(),
    interval: z.object({ low: z.number(), high: z.number() }).nullable(),
    adjusted: z.boolean(),
});
/**
 * One arm pair's numbers. Every effect field is null until both arms have
 * the minimum rows, and `enough` says what a null means: the number does
 * not exist yet. A lift of zero is reported as 0.
 */
export const CortexArmStats = z.object({
    /** Randomised sends and holds in the contrast, after exclusions. */
    sends: z.number(),
    holds: z.number(),
    /** Members contributing at least one eligible row. */
    members: z.number(),
    /** Of all sends in the group (staged aside), the share reported on screen. Null with no sends. */
    seenShare: z.number().nullable(),
    /** Both arms at or above the minimum rows. The effect fields are null when false. */
    enough: z.boolean(),
    meanSent: z.number().nullable(),
    meanHeld: z.number().nullable(),
    lift: z.number().nullable(),
    interval: z.object({ low: z.number(), high: z.number() }).nullable(),
    /** True when the regression-adjusted (AIPW) estimator ran on pre-offer covariates; false when it fell back to Hájek. Absent from a server older than the field. */
    adjusted: z.boolean().optional(),
    /** Sends plus holds needed for an interval that excludes zero at the target lift. Null until there is a variance to scale from. */
    rowsNeeded: z.number().nullable(),
    excluded: CortexLiftExclusions,
    /** Null when the rows have no single unit to measure in. Absent from a server older than the field. */
    magnitude: CortexMagnitudeLift.nullable().optional(),
    /** Acted rows on the sent arm, by who set the outcome. Older rows with no source are in neither. */
    acted: z.object({
        tapped: z.number(),
        closedByReading: z.number(),
    }),
    /** Why each arm's windows closed, staged rows aside, so a sent arm that keeps missing its reading shows beside a held arm that does not. */
    resolutions: z.object({
        sent: CortexResolutionMix,
        held: CortexResolutionMix,
    }),
    /** Sent-arm burden, from the explicit watcher. Each is null when its denominator is zero. */
    guardrails: z.object({
        dismissedOverSeen: z.number().nullable(),
        expiredOverSeen: z.number().nullable(),
        /** Delivered sends nobody saw, over sends. */
        unseenShare: z.number().nullable(),
    }),
});
export const CortexVariantPerformance = CortexArmStats.extend({
    /** Null on rows older than the variant column, grouped together as one unnamed variant. */
    variantId: z.string().nullable(),
    /** How the words would be produced: "template", or model@promptHash. Null when the row has no parts. */
    copy: z.string().nullable(),
    /** The channel the interrupt gate chose before the coin. */
    channel: z.string().nullable(),
});
export const CortexPlayPerformance = z.object({
    rule: z.string(),
    /** The play's measure as it stands in the catalogue now, or null when the play has left it. */
    measure: z
        .object({
        pillar: z.string(),
        metric: z.string(),
        windowMinutes: z.number(),
        target: z.number().nullable(),
    })
        .nullable(),
    /** Every variant's rows together. */
    rollup: CortexArmStats,
    variants: z.array(CortexVariantPerformance),
});
export const CortexBucketSplit = z.object({
    bucket: CortexSnapshotBucket,
    /** The variant the split is narrowed to, or null for every row. */
    variantId: z.string().nullable(),
    values: z.array(CortexArmStats.extend({
        /** The bucket value as a string; rows with no readable snapshot are "unknown". */
        value: z.string(),
    })),
});
export const CortexPerformanceResponse = z.object({
    generatedAt: z.string(),
    windowDays: z.number(),
    sinceIso: z.string(),
    /** Rows per arm below which no effect is shown. */
    minimumRowsPerArm: z.number(),
    /** The lift `rowsNeeded` is planned against. */
    targetLift: z.number(),
    plays: z.array(CortexPlayPerformance),
    /** Present only when a bucket was asked for. */
    split: CortexBucketSplit.nullable(),
    /** What this page cannot show and where to find it instead. */
    notes: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// Member signals: every row one member has written to the signals table, with
// counts by kind and by source. Behind ops:member_data and a tenant check,
// because a signal is a reading from the member's own body or calendar.
// ---------------------------------------------------------------------------
export const CortexMemberSignalsQuery = z.object({
    /** Window to read over, in days. Default 7, at most 90. */
    days: z.coerce.number().int().min(1).max(90).optional(),
    /** Narrow the rows to one kind. The counts are always the whole window. */
    kind: z.string().min(1).max(80).optional(),
    /** Narrow the rows to one source. The counts are always the whole window. */
    source: z.string().min(1).max(80).optional(),
    /** Rows to return, newest first. Default 200, at most 1000. */
    limit: z.coerce.number().int().min(1).max(1000).optional(),
});
export const CortexMemberSignalRow = z.object({
    /** The signals row id, as a string: it is a bigint on the table. */
    id: z.string(),
    kind: z.string(),
    source: z.string(),
    /** When the reading happened, by the device or the member. */
    occurredAt: z.string(),
    /** When the row landed in HealthOS. */
    recordedAt: z.string(),
    payload: z.record(z.string(), z.unknown()),
});
export const CortexMemberSignalsResponse = z.object({
    generatedAt: z.string(),
    /** The member, masked. The caller typed the id; the wire does not repeat it. */
    memberRef: z.string(),
    windowDays: z.number(),
    sinceIso: z.string(),
    /** Rows in the window that match the kind and source filters. */
    total: z.number(),
    /** Rows in the window by kind, before any filter. */
    byKind: z.record(z.string(), z.number()),
    /** Rows in the window by source, before any filter. */
    bySource: z.record(z.string(), z.number()),
    /** The member's whole history, so a quiet window reads apart from an empty member. */
    lifetime: z.object({
        total: z.number(),
        firstAt: z.string().nullable(),
        lastAt: z.string().nullable(),
    }),
    rows: z.array(CortexMemberSignalRow),
});
// ---------------------------------------------------------------------------
// Member decisions: one member's decision records (sends, holds and stopped
// plays) with how each resolved, the ticks that produced them, and the tick
// cursor. The O6 member timeline. Behind ops:member_data and a tenant check.
// ---------------------------------------------------------------------------
export const CortexMemberAssignment = z.enum(["send", "hold", "stopped"]);
export const CortexMemberDecisionsQuery = z.object({
    /** Window to read over, in days. Default 7, at most 90. */
    days: z.coerce.number().int().min(1).max(90).optional(),
    /** Narrow the rows to one arm. The counts are always the whole window. */
    assignment: CortexMemberAssignment.optional(),
    /** Narrow the rows to one play. The counts are always the whole window. */
    rule: z.string().min(1).max(80).optional(),
    /** Rows to return, newest first. Default 200, at most 500. */
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
export const CortexMemberDecisionRow = z.object({
    /** The decision record id. Opens in the decision trace. */
    id: z.string(),
    offeredAt: z.string(),
    rule: z.string(),
    behaviour: z.string().nullable(),
    assignment: z.string(),
    stopReason: z.string().nullable(),
    channel: z.string(),
    variantId: z.string().nullable(),
    sendProbability: z.number().nullable(),
    available: z.boolean(),
    staged: z.boolean(),
    deliveredAt: z.string().nullable(),
    seenAt: z.string().nullable(),
    outcome: z.string().nullable(),
    outcomeAt: z.string().nullable(),
    outcomeSource: z.string().nullable(),
    outcomeMetric: z.string().nullable(),
    windowEndsAt: z.string().nullable(),
    nextAttemptAt: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    resolutionReason: z.string().nullable(),
    baseline: z.number().nullable(),
    observed: z.number().nullable(),
    score: z.number().nullable(),
    reward: z.number().nullable(),
    /** The rest are absent from a server older than them. */
    cardOnly: CortexCardOnly.nullable().optional(),
    readingUnit: z.string().nullable().optional(),
    baselineMethod: z.string().nullable().optional(),
    /** The outcome in the metric's own unit (minutes to sleep, workout minutes). */
    magnitude: z.number().nullable().optional(),
    /** The frozen contract's target, in the metric's own unit. */
    target: z.number().nullable().optional(),
    /** The snapshot's budget band at the coin (open, last, spent, unknown). Spent means the daily push cap was used up. */
    budget: z.string().nullable().optional(),
});
/** One heartbeat tick from the decision audit, enough to say why it was quiet. */
export const CortexMemberTickRow = z.object({
    id: z.string(),
    tickedAt: z.string(),
    window: z.string().nullable(),
    cohort: z.string(),
    agentAction: z.string(),
    shortCircuitReason: z.string().nullable(),
    fired: z.boolean(),
    surfaced: z.boolean(),
    llmInvoked: z.boolean(),
});
export const CortexMemberDecisionsResponse = z.object({
    generatedAt: z.string(),
    /** The member, masked. The caller typed the id; the wire does not repeat it. */
    memberRef: z.string(),
    windowDays: z.number(),
    sinceIso: z.string(),
    /** The heartbeat cursor, or null when the member has never been enrolled in a tick. */
    cursor: z
        .object({
        cohort: z.string(),
        lastTickAt: z.string().nullable(),
        lastTickReason: z.string().nullable(),
        nextDueAt: z.string(),
        firstTick: z.boolean(),
    })
        .nullable(),
    /** Decision rows in the window that match the assignment and rule filters. */
    total: z.number(),
    /** Decision rows in the window by arm, before any filter. */
    byAssignment: z.object({ send: z.number(), hold: z.number(), stopped: z.number() }),
    /** Decision rows in the window by play, before any filter. */
    byRule: z.record(z.string(), z.number()),
    /** Stopped rows in the window by stop reason. */
    byStopReason: z.record(z.string(), z.number()),
    /** Resolved rows in the window by resolution reason. */
    byResolution: z.record(z.string(), z.number()),
    rows: z.array(CortexMemberDecisionRow),
    /** Ticks in the window: how often the loop looked, how often it fired, and the newest ticks. */
    ticks: z.object({
        total: z.number(),
        fired: z.number(),
        rows: z.array(CortexMemberTickRow),
    }),
});
//# sourceMappingURL=cortex.js.map